import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';
import RecognizedIcon from 'dibs-icons/exports/RecognizedRibbon';
import TopSellerIcon from 'dibs-icons/exports/Diamond';
import DiamondFilled from 'dibs-icons/exports/DiamondFilled';
import VettedIcon from 'dibs-icons/exports/VettedCheckmark';
import AssociationsIcon from 'dibs-icons/exports/LeafCrown';

import {
    associationsDescription,
    recognizedTitle,
    recognizedDescription,
    vettedTitle,
    vettedDescription,
    goldSellerTitle,
    platinumSellerTitle,
    diamondSellerTitle,
    tieredSellerDescription,
} from '../messages/pdpDistinctionsMessages';
import { PdpSharedDealerDistinction } from './PdpSharedDealerDistinction';
import styles from './PdpSharedDealerDistinctions.scss';
import { FormattedMessage } from 'react-intl';

function PdpSharedDealerDistinctionsComponent({ seller, showDescriptions }) {
    const isRecognized = get(seller, 'isDistinguished');
    const isRewardsTier2 = get(seller, 'isRewardsTier2');
    const sellerTierDataFound = !get(seller, 'sellerTiers.sellerTierDataNotFound');
    const isGoldSeller = sellerTierDataFound && get(seller, 'sellerTiers.isGoldTier');
    const isPlatinumSeller = sellerTierDataFound && get(seller, 'sellerTiers.isPlatinumTier');
    const tradeAssociations = get(seller, 'tradeAssociations') || [];

    const associationsList = tradeAssociations
        .reduce((acc, { displayName } = {}) => {
            acc.push(displayName);
            return acc;
        }, [])
        .join(', ');

    const associations = (
        <FormattedMessage
            id="PdpSharedDealerDistinctions.associationsListTitle"
            defaultMessage={'Associations: {associationsList}'}
            values={{ associationsList }}
        />
    );

    return (
        <div className={styles.container}>
            <PdpSharedDealerDistinction
                icon={VettedIcon}
                title={vettedTitle}
                description={showDescriptions && vettedDescription}
                dataTn="pdp-distinction-vetted"
            />
            {isGoldSeller && !isRewardsTier2 && (
                <PdpSharedDealerDistinction
                    icon={TopSellerIcon}
                    title={goldSellerTitle}
                    description={showDescriptions && tieredSellerDescription}
                    dataTn="pdp-distinction-gold-tier"
                    customIconStyles={styles.goldDiamond}
                />
            )}
            {isPlatinumSeller && !isRewardsTier2 && (
                <PdpSharedDealerDistinction
                    icon={TopSellerIcon}
                    title={platinumSellerTitle}
                    description={showDescriptions && tieredSellerDescription}
                    dataTn="pdp-distinction-platinum-tier"
                    customIconStyles={styles.platinumDiamond}
                />
            )}
            {isRewardsTier2 && (
                <PdpSharedDealerDistinction
                    icon={DiamondFilled}
                    title={diamondSellerTitle}
                    description={showDescriptions && tieredSellerDescription}
                    dataTn="pdp-distinction-diamond-tier"
                />
            )}
            {isRecognized && (
                <PdpSharedDealerDistinction
                    icon={RecognizedIcon}
                    title={recognizedTitle}
                    description={showDescriptions && recognizedDescription}
                    dataTn="pdp-distinction-recognized"
                />
            )}
            {tradeAssociations.length ? (
                <PdpSharedDealerDistinction
                    icon={AssociationsIcon}
                    title={associations}
                    description={showDescriptions && associationsDescription}
                    dataTn="pdp-distinction-associations"
                />
            ) : null}
        </div>
    );
}

PdpSharedDealerDistinctionsComponent.defaultProps = {
    showDescriptions: false,
};

PdpSharedDealerDistinctionsComponent.propTypes = {
    seller: PropTypes.object.isRequired,
    showDescriptions: PropTypes.bool,
};

export const PdpSharedDealerDistinctions = createFragmentContainer(
    PdpSharedDealerDistinctionsComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDistinctions_seller on Seller {
                isDistinguished
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                sellerTiers {
                    isGoldTier
                    isPlatinumTier
                    sellerTierDataNotFound
                }
                tradeAssociations {
                    serviceId
                    displayName
                }
            }
        `,
    }
);
